import type { ClientSignals } from '@smart-link/shared';
import { getBotdVerdict } from './botd';
import { detectAppName } from './browser';
import { getClickStats } from './click-capture';
import { checkGooglePayReady } from './google-pay';
import { getMouseStats } from './mouse-trajectory';
import { getProbeResult } from './probe';
import { getRafJitter } from './raf-jitter';
import { getScrollStats } from './scroll-capture';
import { getLastTouchProps } from './touch-capture';

const startTs = performance.now();
let mouseEvents = 0;
let touchEvents = 0;
let scrollEvents = 0;
let keyEvents = 0;
let visibilityChanges = 0;
let lastPointerType: string | undefined;

let primedSpeechVoiceCount: number | undefined;

let motionSamples = 0;
let motionMaxDelta = 0;
let lastMotion: { x: number; y: number; z: number } | null = null;
let orientationSamples = 0;

interface ChallengeState {
  passed: boolean;
  ms: number;
  kind: 'click' | 'touch' | 'timeout' | 'none';
}

let challenge: ChallengeState = { passed: false, ms: 0, kind: 'none' };

export function recordChallenge(state: ChallengeState) {
  challenge = state;
}

/** Lightweight behavior snapshot for the pageHide beacon. Lets未验证(秒退) visits —
 *  which never reach verify — still record dwell time + interaction counts, without
 *  running the full (heavy) collectSignals(). Reads the same module-level counters. */
export function getBehaviorSnapshot(): {
  dwellMs: number;
  mouseEvents: number;
  touchEvents: number;
  scrollEvents: number;
} {
  return {
    dwellMs: Math.round(performance.now() - startTs),
    mouseEvents,
    touchEvents,
    scrollEvents,
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('mousemove', () => mouseEvents++, { passive: true });
  window.addEventListener('touchstart', () => touchEvents++, { passive: true });
  window.addEventListener('scroll', () => scrollEvents++, { passive: true });
  window.addEventListener('keydown', () => keyEvents++, { passive: true });
  window.addEventListener('pointerdown', (e) => {
    lastPointerType = e.pointerType || lastPointerType;
  });
  document.addEventListener('visibilitychange', () => visibilityChanges++);

  window.addEventListener(
    'devicemotion',
    (e) => {
      const a = e.accelerationIncludingGravity ?? e.acceleration;
      if (!a) return;
      const x = a.x ?? 0;
      const y = a.y ?? 0;
      const z = a.z ?? 0;
      motionSamples++;
      if (lastMotion) {
        const dx = x - lastMotion.x;
        const dy = y - lastMotion.y;
        const dz = z - lastMotion.z;
        const mag = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (mag > motionMaxDelta) motionMaxDelta = mag;
      }
      lastMotion = { x, y, z };
    },
    { passive: true },
  );
  window.addEventListener('deviceorientation', () => orientationSamples++, { passive: true });

  // speechSynthesis.getVoices() is populated asynchronously — the first call on a
  // fresh page (notably desktop/Android Chrome) returns [] until `voiceschanged`
  // fires. Prime it at load and cache the first non-empty count so the real read
  // at collect time doesn't misreport a legit browser as "0 voices".
  try {
    const ss = window.speechSynthesis;
    if (ss) {
      const readVoices = () => {
        const n = ss.getVoices().length;
        if (n > 0) primedSpeechVoiceCount = n;
      };
      readVoices();
      ss.addEventListener?.('voiceschanged', readVoices);
    }
  } catch {
    // ignore
  }
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function canvasFingerprint(): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 240, 60);
    ctx.fillStyle = '#069';
    ctx.fillText('TikTok-Cloak ⚡️', 2, 2);
    ctx.fillStyle = 'rgba(102,204,0,0.7)';
    ctx.fillText('TikTok-Cloak ⚡️', 4, 17);
    return canvas.toDataURL();
  } catch {
    return undefined;
  }
}

function webglFingerprint(): { raw: string | undefined; summary: string | undefined } {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return { raw: undefined, summary: undefined };
    const dbgExt = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = dbgExt
      ? gl.getParameter(dbgExt.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);
    const renderer = dbgExt
      ? gl.getParameter(dbgExt.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    const params = [
      vendor,
      renderer,
      gl.getParameter(gl.VERSION),
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      gl.getParameter(gl.MAX_TEXTURE_SIZE),
      gl.getParameter(gl.MAX_VIEWPORT_DIMS)?.toString?.(),
    ];
    const summary = `${vendor ?? ''} ${renderer ?? ''}`.toLowerCase().slice(0, 120);
    return { raw: params.join('|'), summary };
  } catch {
    return { raw: undefined, summary: undefined };
  }
}

async function audioFingerprint(): Promise<string | undefined> {
  try {
    const Ctx =
      window.OfflineAudioContext ||
      (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext })
        .webkitOfflineAudioContext;
    if (!Ctx) return undefined;
    const ctx = new Ctx(1, 5000, 44100);
    const oscillator = ctx.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(10000, ctx.currentTime);
    const compressor = ctx.createDynamicsCompressor();
    oscillator.connect(compressor);
    compressor.connect(ctx.destination);
    oscillator.start(0);
    const buf = await ctx.startRendering();
    const data = buf.getChannelData(0);
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += Math.abs(data[i] ?? 0);
    return sum.toString();
  } catch {
    return undefined;
  }
}

function detectFonts(): string {
  const baseFonts = ['monospace', 'sans-serif', 'serif'] as const;
  const testFonts = [
    'Arial',
    'Verdana',
    'Helvetica',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Palatino',
    'Garamond',
    'Bookman',
    'Comic Sans MS',
    'Trebuchet MS',
    'Impact',
    'Tahoma',
    'PingFang SC',
    'Microsoft YaHei',
    'Hiragino Sans',
    'Noto Sans',
    'Roboto',
    'SF Pro',
    'Segoe UI',
  ];
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const body = document.body;
  const defaultWidth: Record<string, number> = {};
  const defaultHeight: Record<string, number> = {};
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.left = '-9999px';
  span.style.fontSize = testSize;
  span.style.visibility = 'hidden';
  span.textContent = testString;
  body.appendChild(span);
  for (const base of baseFonts) {
    span.style.fontFamily = base;
    defaultWidth[base] = span.offsetWidth;
    defaultHeight[base] = span.offsetHeight;
  }
  const available: string[] = [];
  for (const font of testFonts) {
    let detected = false;
    for (const base of baseFonts) {
      span.style.fontFamily = `'${font}', ${base}`;
      if (span.offsetWidth !== defaultWidth[base] || span.offsetHeight !== defaultHeight[base]) {
        detected = true;
        break;
      }
    }
    if (detected) available.push(font);
  }
  body.removeChild(span);
  return available.join(',');
}

async function getBattery(): Promise<{
  hasBattery: boolean;
  batteryCharging?: boolean;
  batteryLevel?: number;
}> {
  try {
    const bm = (navigator as Navigator & { getBattery?: () => Promise<BatteryManager> }).getBattery;
    if (!bm) return { hasBattery: false };
    const b = await bm.call(navigator);
    return { hasBattery: true, batteryCharging: b.charging, batteryLevel: b.level };
  } catch {
    return { hasBattery: false };
  }
}

interface BatteryManager {
  charging: boolean;
  level: number;
}

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

function getConnection(): {
  connectionType?: string;
  connectionDownlink?: number;
  connectionRtt?: number;
  saveData?: boolean;
} {
  const conn = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!conn) return {};
  return {
    connectionType: conn.effectiveType,
    connectionDownlink: conn.downlink,
    connectionRtt: conn.rtt,
    saveData: conn.saveData,
  };
}

async function getMediaDevices(): Promise<{
  mediaInputs?: number;
  mediaOutputs?: number;
  mediaCameras?: number;
}> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return {};
    const devices = await navigator.mediaDevices.enumerateDevices();
    let inputs = 0;
    let outputs = 0;
    let cams = 0;
    for (const d of devices) {
      if (d.kind === 'audioinput') inputs++;
      else if (d.kind === 'audiooutput') outputs++;
      else if (d.kind === 'videoinput') cams++;
    }
    return { mediaInputs: inputs, mediaOutputs: outputs, mediaCameras: cams };
  } catch {
    return {};
  }
}

function hasStorage(kind: 'localStorage' | 'sessionStorage' | 'indexedDB'): boolean {
  try {
    if (kind === 'indexedDB') return 'indexedDB' in window && window.indexedDB != null;
    const s = window[kind];
    if (!s) return false;
    const k = '__tc_test__';
    s.setItem(k, '1');
    s.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function platformUaConsistent(ua: string, platform?: string): boolean {
  if (!platform) return true;
  const p = platform.toLowerCase();
  const u = ua.toLowerCase();
  if (p.includes('win') && !u.includes('windows')) return false;
  if (p.includes('mac') && !u.includes('mac os') && !u.includes('macintosh')) return false;
  if (p.includes('linux') && !u.includes('linux') && !u.includes('android')) return false;
  if (p.includes('iphone') && !u.includes('iphone')) return false;
  if (p.includes('ipad') && !u.includes('ipad')) return false;
  return true;
}

/**
 * Measure timing jitter by repeatedly running a fixed compute workload and
 * recording the wall-time spent. Real CPUs jitter (cache misses, scheduler,
 * thermal); headless emulators often show suspiciously flat timings.
 * Returns the standard deviation of sample durations in ms.
 */
// --- Driver / anti-detect tool marker scan -----------------------------------
const DRIVER_MARKER_KEYS = [
  '_phantom',
  '__nightmare',
  '__selenium_unwrapped',
  '_selenium',
  'callSelenium',
  '_Selenium_IDE_Recorder',
  '__webdriver_evaluate',
  '__driver_evaluate',
  '__webdriver_script_function',
  '__webdriver_script_func',
  '__webdriver_script_fn',
  '__fxdriver_evaluate',
  '__fxdriver_unwrapped',
  '__driver_unwrapped',
  '__webdriver_unwrapped',
  'webdriver',
  'webdriverFunc',
  'domAutomation',
  'domAutomationController',
  '$cdc_asdjflasutopfhvcZLmcfl_',
] as const;

function detectDriverMarkers(): string[] {
  const found: string[] = [];
  try {
    const w = window as unknown as Record<string, unknown>;
    for (const k of DRIVER_MARKER_KEYS) {
      // 'webdriver' is on navigator too — skip checking on window unless distinct
      if (k === 'webdriver') continue;
      if (k in w) found.push(k);
    }
    // wildcards: cdc_* injected by ChromeDriver
    for (const k of Object.getOwnPropertyNames(w)) {
      if (k.startsWith('cdc_') || k.startsWith('$cdc')) found.push(k);
    }
    // CDP runtime injects $chrome_asyncScriptInfo etc.
    if ('$chrome_asyncScriptInfo' in w) found.push('$chrome_asyncScriptInfo');
    // navigator.webdriver getter exists (boolean only present in driven Chromium)
    if (typeof (navigator as Navigator & { webdriver?: boolean }).webdriver === 'boolean') {
      // already captured as `webdriver` field, but flagging if the descriptor is overridden
    }
  } catch {
    // ignore
  }
  return Array.from(new Set(found));
}

// --- Native function integrity check -----------------------------------------
function isNativeFn(fn: unknown): boolean {
  if (typeof fn !== 'function') return false;
  try {
    return /\{\s*\[native code\]\s*\}/.test(Function.prototype.toString.call(fn));
  } catch {
    return false;
  }
}

function detectNativeFnTamper(): string[] {
  const tampered: string[] = [];
  const check = (name: string, fn: unknown) => {
    if (fn != null && !isNativeFn(fn)) tampered.push(name);
  };
  try {
    check('Function.prototype.toString', Function.prototype.toString);
    check('Object.defineProperty', Object.defineProperty);
    check('Object.getOwnPropertyDescriptor', Object.getOwnPropertyDescriptor);
    check(
      'Navigator.prototype.webdriver getter',
      Object.getOwnPropertyDescriptor(Navigator.prototype, 'webdriver')?.get,
    );
    check('Permissions.prototype.query', navigator.permissions?.query);
    check('HTMLCanvasElement.prototype.toDataURL', HTMLCanvasElement.prototype.toDataURL);
    check(
      'CanvasRenderingContext2D.prototype.getImageData',
      typeof CanvasRenderingContext2D !== 'undefined'
        ? CanvasRenderingContext2D.prototype.getImageData
        : undefined,
    );
    check('Date.prototype.getTimezoneOffset', Date.prototype.getTimezoneOffset);
    check(
      'Notification.requestPermission',
      typeof Notification !== 'undefined' ? Notification.requestPermission : undefined,
    );
  } catch {
    // ignore
  }
  return tampered;
}

// --- WebGPU adapter info -----------------------------------------------------
interface GPU {
  requestAdapter: () => Promise<GPUAdapter | null>;
}
interface GPUAdapter {
  info?: { vendor?: string; architecture?: string; device?: string; description?: string };
  requestAdapterInfo?: () => Promise<GPUAdapter['info']>;
}

async function getWebGpu(): Promise<{ vendor?: string; architecture?: string }> {
  try {
    const nav = navigator as Navigator & { gpu?: GPU };
    if (!nav.gpu) return {};
    const adapter = await nav.gpu.requestAdapter();
    if (!adapter) return {};
    const info = adapter.info ?? (await adapter.requestAdapterInfo?.());
    return {
      vendor: info?.vendor,
      architecture: info?.architecture,
    };
  } catch {
    return {};
  }
}

// --- Speech synthesis voices -------------------------------------------------
function getSpeechVoiceCount(): number | undefined {
  // Prefer the primed non-zero count (see the voiceschanged listener above).
  if (primedSpeechVoiceCount != null) return primedSpeechVoiceCount;
  try {
    if (!window.speechSynthesis) return undefined;
    return window.speechSynthesis.getVoices().length;
  } catch {
    return undefined;
  }
}

// --- WebAuthn platform authenticator -----------------------------------------
async function hasPlatformAuthenticator(): Promise<boolean | undefined> {
  try {
    const PKC = window.PublicKeyCredential;
    if (!PKC?.isUserVerifyingPlatformAuthenticatorAvailable) return undefined;
    return await PKC.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return undefined;
  }
}

// --- CSS feature support snapshot --------------------------------------------
function getCssFeatures(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const supports = (q: string) => {
    try {
      return CSS.supports(q);
    } catch {
      return false;
    }
  };
  // Features with known browser-version availability — used to detect spoofed UAs
  out['selector:has'] = supports('selector(:has(*))'); // Chrome 105+, Safari 15.4+
  out.oklch = supports('color: oklch(0 0 0)'); // Chrome 111+, Safari 15.4+
  out.containerType = supports('container-type: inline-size'); // Chrome 105+
  out.subgrid = supports('grid-template-rows: subgrid'); // Chrome 117+, Safari 16+
  out.dynVH = supports('height: 100dvh'); // Chrome 108+, Safari 15.4+
  return out;
}

function measurePerfJitter(): number {
  const SAMPLES = 24;
  const INNER = 8000;
  const samples: number[] = [];
  for (let i = 0; i < SAMPLES; i++) {
    const start = performance.now();
    let acc = 0;
    for (let j = 1; j < INNER; j++) {
      acc += Math.sqrt(j) * 1.0000001;
    }
    const end = performance.now();
    // keep acc alive so the loop isn't dead-code eliminated
    if (acc < 0) console.log(acc);
    samples.push(end - start);
  }
  const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
  const variance = samples.reduce((a, b) => a + (b - mean) ** 2, 0) / samples.length;
  return Number(Math.sqrt(variance).toFixed(4));
}

/**
 * 单个异步指纹探测的超时上限。iOS 某些 in-app webview(如 TikTok trill)里
 * OfflineAudioContext.startRendering 等会永不 resolve,无兜底就把 collectSignals
 * (进而整个 verify)无限挂起 —— 访客永远停在 "Checking..."、verify 从不落库(丢数据)。
 */
const SIGNAL_TIMEOUT_MS = 2000;

export async function collectSignals(): Promise<ClientSignals> {
  const nav = navigator;
  const ua = nav.userAgent;
  // 任一异步探测超时即置位:verify 仍带已收集的部分信号照常走引擎判定(不跳过、不放行),
  // 该 flag 随信号落库,让降级访问在 audits.reasons 里可辨(signalCollectTimeout 规则)。
  let signalCollectTimedOut = false;
  const withTimeout = <T>(p: Promise<T>, fallback: T, ms = SIGNAL_TIMEOUT_MS): Promise<T> =>
    Promise.race([
      p.catch(() => fallback),
      new Promise<T>((resolve) => {
        setTimeout(() => {
          signalCollectTimedOut = true;
          resolve(fallback);
        }, ms);
      }),
    ]);
  const probeData = getProbeResult();
  const [canvasRaw, audioRaw, battery, media, webgpu, platformAuth, permissions, botd, googlePay] =
    await Promise.all([
      Promise.resolve(canvasFingerprint()),
      withTimeout(audioFingerprint(), undefined),
      withTimeout(getBattery(), { hasBattery: false }),
      withTimeout(getMediaDevices(), {}),
      withTimeout(getWebGpu(), {}),
      withTimeout(hasPlatformAuthenticator(), undefined),
      withTimeout(getPermissionStates(), {}),
      getBotdVerdict(),
      withTimeout(checkGooglePayReady(ua), { ready: false, status: 'timeout' as const }, 2500),
    ]);

  const webgl = webglFingerprint();
  const fontsRaw = detectFonts();
  const connection = getConnection();
  const perfNowJitter = measurePerfJitter();

  // Canvas noise detection: render twice, compare
  const canvasRaw2 = canvasFingerprint();
  const canvasStable =
    canvasRaw != null && canvasRaw2 != null ? canvasRaw === canvasRaw2 : undefined;

  // sha256Hex 走 crypto.subtle.digest —— 同样可能在恶劣 webview 挂起,加超时兜底
  // (上一轮只包了异步探测、漏了这组哈希)。超时置 signalCollectTimedOut,verify 继续。
  const [canvasHash, webglHash, audioHash, fontsHash] = await Promise.all([
    canvasRaw ? withTimeout(sha256Hex(canvasRaw), undefined) : Promise.resolve(undefined),
    webgl.raw ? withTimeout(sha256Hex(webgl.raw), undefined) : Promise.resolve(undefined),
    audioRaw ? withTimeout(sha256Hex(audioRaw), undefined) : Promise.resolve(undefined),
    withTimeout(sha256Hex(fontsRaw), undefined),
  ]);

  // Mobile-specific signals
  const hoverNone = window.matchMedia?.('(hover: none)').matches;
  const pointerCoarse = window.matchMedia?.('(pointer: coarse)').matches;
  const hasTouchEventCtor = 'ontouchstart' in window;
  const hasVibrate = typeof nav.vibrate === 'function';
  const orientation = (screen as Screen & { orientation?: { type: string; angle: number } })
    .orientation;
  const vv = (window as Window & { visualViewport?: { scale?: number } }).visualViewport;
  const iosStandalone = (nav as Navigator & { standalone?: boolean }).standalone;

  let hasNotificationPermissionMismatch = false;
  try {
    if (nav.permissions && 'Notification' in window) {
      const status = await withTimeout<PermissionStatus | undefined>(
        nav.permissions.query({ name: 'notifications' as PermissionName }),
        undefined,
      );
      hasNotificationPermissionMismatch =
        status != null && Notification.permission === 'denied' && status.state === 'prompt';
    }
  } catch {
    // ignore
  }

  const platform = nav.platform;

  return {
    userAgent: ua,
    platform,
    languages: Array.from(nav.languages ?? []),
    hardwareConcurrency: nav.hardwareConcurrency,
    deviceMemory: (nav as Navigator & { deviceMemory?: number }).deviceMemory,
    pluginsCount: nav.plugins?.length,
    webdriver: nav.webdriver === true,
    screenWidth: screen.width,
    screenHeight: screen.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    colorDepth: screen.colorDepth,
    devicePixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvasHash,
    webglHash,
    audioHash,
    fontsHash,
    maxTouchPoints: nav.maxTouchPoints,
    hasChromeRuntime: 'chrome' in window,
    hasNotificationPermissionMismatch,
    ...getBehaviorSnapshot(),
    keyEvents,
    pointerType: lastPointerType,
    visibilityChanges,
    motionSamples,
    motionMaxDelta,
    orientationSamples,
    hasBattery: battery.hasBattery,
    batteryCharging: battery.batteryCharging,
    batteryLevel: battery.batteryLevel,
    ...connection,
    ...media,
    hasLocalStorage: hasStorage('localStorage'),
    hasSessionStorage: hasStorage('sessionStorage'),
    hasIndexedDB: hasStorage('indexedDB'),
    platformUaMatch: platformUaConsistent(ua, platform),
    perfNowJitter,
    webglRendererSummary: webgl.summary,
    hoverNone,
    pointerCoarse,
    hasTouchEventCtor,
    hasVibrate,
    orientationType: orientation?.type,
    orientationAngle: orientation?.angle,
    visualViewportScale: vv?.scale,
    iosStandalone,
    challengePassed: challenge.passed,
    challengeMs: challenge.ms,
    challengeKind: challenge.kind,
    isTikTokWebview: /BytedanceWebview|musical_ly|aweme|tiktok|trill/i.test(ua),
    // Broad in-app browser detection (single source = breakout logic). Empty when
    // not an in-app webview; otherwise the detected app key (tiktok/facebook/…).
    inAppBrowser: detectAppName(ua) ?? '',
    // Advanced
    driverMarkers: detectDriverMarkers(),
    nativeFnTampered: detectNativeFnTamper(),
    webgpuVendor: webgpu.vendor,
    webgpuArchitecture: webgpu.architecture,
    speechVoicesCount: getSpeechVoiceCount(),
    cssFeatures: getCssFeatures(),
    hasPlatformAuthenticator: platformAuth,
    signalCollectTimedOut,
    touchForce: getLastTouchProps()?.force,
    touchRadiusX: getLastTouchProps()?.radiusX,
    touchRadiusY: getLastTouchProps()?.radiusY,
    mouseSampleCount: getMouseStats().count,
    mouseAngleStdDev: getMouseStats().angleStdDev,
    mouseSpeedStdDev: getMouseStats().speedStdDev,
    // --- v2 signals ---
    rafJitter: getRafJitter(),
    canvasStable,
    scrollSpeedStdDev: getScrollStats().speedStdDev,
    scrollSamples: getScrollStats().samples,
    ...permissions,
    hasClipboard: typeof nav.clipboard?.writeText === 'function',
    pdfViewerEnabled: (nav as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled,
    mimeTypesCount: nav.mimeTypes?.length,
    clickPositionStdDev: getClickStats().positionStdDev,
    clickSamples: getClickStats().samples,
    // Probe cross-check (filled from probe response). When the probe couldn't
    // resolve expected tz/lang for the IP country (country missing from the geo
    // maps), leave these `undefined` (unknown) instead of reporting `false` —
    // otherwise every visitor from an unmapped country trips timezone/language
    // mismatch. The engine then falls back to its own server-side lookup.
    probeCountry: probeData?.detectedCountry,
    probeTimezoneMatch:
      probeData && probeData.expectedTimezones.length > 0
        ? probeData.expectedTimezones.includes(Intl.DateTimeFormat().resolvedOptions().timeZone)
        : undefined,
    probeLanguageMatch:
      probeData && probeData.expectedLanguages.length > 0
        ? probeData.expectedLanguages.some((lang) =>
            (nav.languages?.[0] ?? '').toLowerCase().startsWith(lang),
          )
        : undefined,
    probeVerifyIpMatch: undefined, // server-side only (probe vs verify IP comparison)
    googlePayReady: googlePay.ready,
    googlePayStatus: googlePay.status,
    googlePayError: googlePay.error,
    // BotD verdict (fail-open: both undefined when BotD didn't load/timed out)
    botdBot: botd.bot,
    botdKind: botd.kind,
  };
}

async function getPermissionStates(): Promise<{
  permissionCamera?: string;
  permissionMicrophone?: string;
  permissionGeolocation?: string;
}> {
  if (!navigator.permissions) return {};
  const query = async (name: string) => {
    try {
      const s = await navigator.permissions.query({ name: name as PermissionName });
      return s.state;
    } catch {
      return undefined;
    }
  };
  const [camera, microphone, geolocation] = await Promise.all([
    query('camera'),
    query('microphone'),
    query('geolocation'),
  ]);
  return {
    permissionCamera: camera,
    permissionMicrophone: microphone,
    permissionGeolocation: geolocation,
  };
}
